/* eslint-disable react/no-danger */
import React from 'react'
import PropTypes from 'prop-types'

import ReactSelect from 'react-select'
import Creatable from 'react-select/creatable'
import FormFieldError from '../FormFieldError'

const SelectOptionPropType = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
})

import './Select.scss'

class Select extends React.Component {
  static propTypes = {
    allowCustomTags: PropTypes.bool,
    autoFocus: PropTypes.bool,
    closeOnSelect: PropTypes.bool, // will default to `true` when multi=true
    defaultValue: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.string),
      PropTypes.string,
      PropTypes.number,
    ]),
    disabled: PropTypes.bool,
    filterOption: PropTypes.func,
    // eslint-disable-next-line react/forbid-prop-types
    input: PropTypes.object, // final-form
    isClearable: PropTypes.bool,
    isLoading: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType, PropTypes.node]),
    meta: PropTypes.object, // final-form
    multi: PropTypes.bool,
    onSelectOption: PropTypes.func, // passes the full object from react-select to handler, ie: { label: String, value: any }
    name: PropTypes.string,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(SelectOptionPropType).isRequired,
    placeholder: PropTypes.string,
    reactSelectCustomComponents: PropTypes.object,
    subText: PropTypes.oneOfType([PropTypes.string, PropTypes.elementType, PropTypes.node]),
    value: PropTypes.any,
    valueAlwaysArray: PropTypes.bool, // if !multi but want the value to be an array
  }

  static defaultProps = {
    allowCustomTags: false,
    autoFocus: false,
    closeOnSelect: true,
    defaultValue: undefined,
    disabled: undefined,
    filterOption: undefined,
    input: {},
    isClearable: true,
    isLoading: false,
    label: '',
    onSelectOption: undefined,
    name: '',
    meta: {},
    multi: false,
    onChange: null,
    placeholder: '',
    reactSelectCustomComponents: {},
    subText: '',
    value: undefined,
    valueAlwaysArray: false,
  }

  handleChange = selectedOption => {
    const { input, onChange, multi, onSelectOption, valueAlwaysArray } = this.props

    // spread the selectedOption object on the target so other properties passed to the option can
    // be used
    const target = { ...selectedOption, value: multi ? [] : '' }

    if (multi) {
      target.value = selectedOption ? selectedOption.map(({ value }) => value) : target.value
    } else {
      const _value = selectedOption && selectedOption.value
      // !_value is "null" when <Select /> is cleared, we don't want [null] passed back
      if (!_value) target.value = valueAlwaysArray ? [] : _value
      else target.value = valueAlwaysArray ? [_value] : _value
    }

    if (onSelectOption) onSelectOption(selectedOption, target.value)

    if (onChange) {
      // simulate something similar to the React synthetic event for compatibility
      onChange({ currentTarget: target, target })
    }
    if (input && input.onChange) {
      // compatability w/ react-final-form & redux-form
      // when used in: <Field component={Select} />s
      input.onChange(target.value)
    }
  }

  getOptionValue = (options, value) => {
    let _value = this.props.multi ? [] : ''

    if (Array.isArray(value)) {
      for (const val of value) {
        let foundOption = options.find(o => o.value === val)

        // we have to create an object for allowCustomTags options
        if (!foundOption) foundOption = { label: val, value: val }

        if (this.props.multi) _value.push(foundOption)
        else _value = foundOption
      }
    } else {
      let foundOption = options.find(o => o.value === value)

      // we have to create an object for allowCustomTags options
      if (!foundOption && value) foundOption = { label: value, value }

      if (this.props.multi) _value.push(foundOption)
      else _value = foundOption
    }

    return _value
  }

  /**
   * A custom renderer used by react-select. Checks if a string contains
   * HTML markup and renders appropriately or just returns the string otherwise.
   * @param label
   * @param value
   */
  optionRenderer = ({ label, value }) => {
    if (/<[a-z][\s\S]*>/i.test(label)) {
      return <div dangerouslySetInnerHTML={{ __html: label }} value={value} />
    }

    return label
  }

  renderLabel = () => {
    const { label } = this.props

    // if label is a component then don't wrap it in <label/>
    if (React.isValidElement(label)) return label

    return (label && <label>{label}</label>) || null
  }

  render() {
    const {
      allowCustomTags,
      autoFocus,
      defaultValue,
      disabled,
      filterOption,
      input,
      isLoading,
      meta,
      multi,
      name,
      options,
      placeholder,
      reactSelectCustomComponents,
      subText,
    } = this.props

    // https://github.com/JedWatson/react-select#user-created-tags
    const Component = allowCustomTags ? Creatable : ReactSelect

    const fieldName = name || input.name

    // we manually set the value because we only pass back the value to react-final-form
    // and so it will only send down the value, we have to filter the options for the value objects
    let value

    // value prop takes precendence (ie: when we're not using Select inside react-final-form)
    if (this.props.value !== undefined) {
      value = this.getOptionValue(options, this.props.value)
    } else if (input.value !== undefined && input.value !== '') {
      value = this.getOptionValue(options, input.value)
    } else if (input) {
      // if input is defined but no value then set the value to empty selection
      // this is needed when final-form sends down the value as `undefined`
      // and react-select keeps the previous value state
      value = ''
    }

    // default to true when multi
    const closeOnSelect =
      typeof this.props.closeOnSelect === 'undefined' ? !this.props.multi : this.props.closeOnSelect
    const defaultValueOption = this.getOptionValue(options, defaultValue)

    return (
      <div className="form-group">
        {this.renderLabel()}

        <Component
          autoFocus={autoFocus}
          className="react-select-input"
          classNamePrefix="react-select"
          closeMenuOnSelect={closeOnSelect}
          components={reactSelectCustomComponents}
          defaultValue={defaultValueOption}
          disabled={meta.submitting}
          filterOption={filterOption}
          isClearable={this.props.isClearable}
          isDisabled={disabled || meta.submitting}
          isLoading={isLoading}
          isMulti={multi}
          name={multi ? `${fieldName}[]` : fieldName}
          options={options}
          optionRenderer={this.optionRenderer}
          placeholder={placeholder}
          ref={el => (this.el = el)}
          {...input}
          onChange={this.handleChange} // must be after {...input} to handle onChange
          theme={theme => ({
            ...theme,
            // borderRadius: 0,
            colors: {
              ...theme.colors,
              text: 'white',
              //   primary25: 'hotpink',
              //   primary: 'black',
            },
          })}
          value={value} // must be after {...input}
        />

        {subText && <small className="form-text text-muted">{subText}</small>}

        <FormFieldError {...meta} />
      </div>
    )
  }
}

export default Select
